// Geo quest AR session (M1): engine + GpsCompassProvider + anchors placed at ENU
// offsets + proximity discovery with dwell + hysteresis (plan §4.4).
//
// Unity-parity behaviors (2026-07-06, after first field test):
// - Alignment requires a STABLE GPS fix (consecutive agreeing fixes), not one
//   lucky reading — see gps.ts.
// - Anchor CONTENT is lazy: nothing is downloaded or shown until the user is
//   within the alternative's camera_distance of that anchor (performance + no
//   spawn-at-feet). Empty holders exist earlier only as invisible math targets
//   for distance/chevron.
// - When content spawns, its position is re-origined to the user's CURRENT
//   GPS fix + camera position (short baseline → less accumulated drift), not to
//   the alignment origin.
// - Discovery only runs for anchors whose content is placed ('ready'), so a
//   mid-download anchor can never be "found".
import * as THREE from 'three'
import type {RawAlternative, RawAnchor} from '../api/types'
import {FT_TO_M} from '../config'
import type {CameraStatus, EngineKind, EngineSession} from '../engine/engine'
import {startEngine} from '../engine/engine'
import type {AlignmentState, DebugSnapshot} from '../placement/gpsCompassProvider'
import {createGpsCompassProvider} from '../placement/gpsCompassProvider'
import {enuOffset, enuToLocal} from '../placement/geo'
import {isGeoLocated, num} from './mapping'
import type {AnchorGroup} from './anchorGroup'
import {buildAnchorGroup} from './anchorGroup'
import type {BuiltAsset} from './assets'
import {buildLabelSprite} from './assets'

const DISCOVERY_FLOOR_M = 10
const DWELL_SECONDS = 1.5
const EXIT_HYSTERESIS = 1.2

type ContentState = 'far' | 'loading' | 'ready' | 'failed'

export interface QuestAnchorState {
  id: number
  name: string
  order: number
  discovered: boolean
  contentState: ContentState
  /** Horizontal scene-space distance from camera, meters (null until positioned). */
  distanceM: number | null
  /** Chevron rotation relative to camera forward, CSS degrees (clockwise). */
  relativeBearingCssDeg: number | null
}

export interface QuestStatus {
  alignment: AlignmentState
  /** Which AR engine actually booted — null until it has. Shown in ?debug=1. */
  engineKind: EngineKind | null
  anchors: QuestAnchorState[]
  foundCount: number
  total: number
  completed: boolean
  driftSuspected: boolean
  /** Names of anchors whose content is currently downloading. */
  loading: string[]
  debug: DebugSnapshot
}

export interface QuestCallbacks {
  onCameraStatus?: (status: CameraStatus) => void
  onStatus?: (status: QuestStatus) => void
  onDiscovered?: (anchor: QuestAnchorState) => void
  onError?: (error: unknown) => void
  /** AR ended from outside the app (WebXR: system back / "Exit AR"). */
  onSessionEnd?: () => void
}

export interface QuestSession {
  recenter: () => void
  /** Clear found-state (localStorage + in-memory) so the quest can be replayed. */
  resetProgress: () => void
  stop: () => void
}

interface TrackedAnchor {
  raw: RawAnchor
  geo: {lat: number; lng: number}
  name: string
  holder: THREE.Object3D
  content: AnchorGroup | null
  label: BuiltAsset | null
  contentState: ContentState
  /** True once the holder has a real ENU position (post-alignment). */
  positioned: boolean
  discovered: boolean
  dwellSeconds: number
  inside: boolean
  distanceM: number | null
  cssDeg: number | null
}

const storageKey = (altId: number | string) => `webar_progress_${altId}`

function loadProgress(altId: number | string): Set<number> {
  try {
    const raw = localStorage.getItem(storageKey(altId))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as {discovered?: number[]}
    return new Set(parsed.discovered ?? [])
  } catch {
    return new Set()
  }
}

function saveProgress(altId: number | string, discovered: Set<number>, completed: boolean) {
  try {
    localStorage.setItem(
      storageKey(altId),
      JSON.stringify({
        discovered: [...discovered],
        completedAt: completed ? new Date().toISOString() : undefined,
      }),
    )
  } catch {
    // storage full/blocked — progress just won't persist
  }
}

export async function startQuestSession(
  canvas: HTMLCanvasElement,
  alternative: RawAlternative,
  anchors: RawAnchor[],
  cb: QuestCallbacks,
): Promise<QuestSession> {
  const geoAnchors = anchors.filter((a) => isGeoLocated(a.lat, a.lng))
  if (!geoAnchors.length) throw new Error('This alternative has no geo-located checkpoints.')

  // camera_distance (feet) is both the content-load radius and the discovery
  // radius — matching Unity's "don't load beyond camera distance" behavior.
  const radiusM = Math.max((num(alternative.camera_distance) ?? 0) * FT_TO_M, DISCOVERY_FLOOR_M)
  const previouslyFound = loadProgress(alternative.id)

  let stopped = false
  const provider = createGpsCompassProvider()

  // Empty holders NOW — invisible math targets. Content loads lazily by distance.
  const tracked: TrackedAnchor[] = geoAnchors.map((raw) => {
    const holder = new THREE.Object3D()
    holder.name = `geo-anchor-${raw.id}`
    holder.visible = false
    provider.worldGroup.add(holder)
    return {
      raw,
      geo: {lat: Number(raw.lat), lng: Number(raw.lng)},
      name: raw.name || `Checkpoint ${raw.id}`,
      holder,
      content: null,
      label: null,
      contentState: 'far' as ContentState,
      positioned: false,
      discovered: previouslyFound.has(raw.id),
      dwellSeconds: 0,
      inside: false,
      distanceM: null,
      cssDeg: null,
    }
  })

  // Registered BEFORE the engine runs a single frame — the original spawn-at-feet
  // bug was this callback attaching after alignment had already happened.
  provider.onStateChange = (s) => {
    if (s === 'aligned') positionAnchors()
    emitStatus()
  }

  // If the engine fails to start (camera denied/blocked), the provider's GPS
  // watch + orientation listeners must not leak — nobody else owns them on the
  // throw path (review finding: OS location indicator stayed on forever).
  let engine: EngineSession
  // emitStatus can run before startEngine resolves (the provider aligns on its
  // own clock), so the kind is read from a variable rather than `engine`.
  let engineKind: EngineKind | null = null
  try {
    engine = await startEngine(canvas, {
      onCameraStatus: cb.onCameraStatus,
      onError: cb.onError,
      onSessionEnd: cb.onSessionEnd,
      onFrame: (dt, camera) => {
        if (stopped) return
        provider.update(camera)
        if (provider.state !== 'aligned') return
        for (const t of tracked) {
          if (!t.positioned) continue
          updateAnchorMetrics(t, camera)
          maybeLoadContent(t, camera)
          if (t.contentState === 'ready') {
            t.content?.tick(dt, camera.position)
            updateDiscovery(t, dt)
          }
        }
      },
    })
  } catch (err) {
    provider.stop()
    throw err
  }
  engineKind = engine.kind
  engine.scene.add(provider.worldGroup)
  // The provider may have aligned while the engine was still resolving.
  if (provider.state === 'aligned') positionAnchors()

  /** Origin-based ENU placement for every anchor (holder-local inside worldGroup). */
  function positionAnchors() {
    const origin = provider.debug.originFix
    if (!origin) return
    for (const t of tracked) {
      const [x, y, z] = enuToLocal(enuOffset(origin, t.geo))
      t.holder.position.set(x, y, z)
      t.positioned = true
      // Re-alignment moved the anchors — stale dwell/inside state from the old
      // positions must not carry over (review finding: recenter could complete
      // a discovery the user never earned at the corrected position).
      if (!t.discovered) {
        t.inside = false
        t.dwellSeconds = 0
      }
    }
  }

  const camForward = new THREE.Vector3()
  const anchorWorld = new THREE.Vector3()

  function updateAnchorMetrics(t: TrackedAnchor, camera: THREE.PerspectiveCamera) {
    t.holder.getWorldPosition(anchorWorld)
    const dx = anchorWorld.x - camera.position.x
    const dz = anchorWorld.z - camera.position.z
    t.distanceM = Math.hypot(dx, dz)
    camera.getWorldDirection(camForward)
    const alphaTo = Math.atan2(dx, dz)
    const alphaF = Math.atan2(camForward.x, camForward.z)
    let rel = alphaTo - alphaF
    rel = Math.atan2(Math.sin(rel), Math.cos(rel)) // normalize to [-π, π]
    // α is CCW-from-above; CSS rotation is clockwise-positive; chevron up = ahead.
    t.cssDeg = (-rel * 180) / Math.PI
  }

  /** Lazy content: start downloading when within camera_distance; never before. */
  function maybeLoadContent(t: TrackedAnchor, camera: THREE.PerspectiveCamera) {
    if (t.contentState !== 'far' || t.distanceM === null || t.distanceM > radiusM) return
    t.contentState = 'loading'
    emitStatus()
    void buildAnchorGroup(t.raw)
      .then((content) => {
        if (stopped) {
          content.dispose()
          return
        }
        t.content = content
        t.holder.add(content.group)
        // Audio/video-only checkpoints render nothing on web — float the
        // checkpoint's name instead so "found" never means empty air.
        if (content.group.children.length === 0) {
          const label = buildLabelSprite(t.name)
          t.label = label
          t.holder.add(label.object)
        }
        // Re-origin to the user: place from the CURRENT stable fix + camera
        // position (short baseline → less drift). Fall back to the alignment
        // origin, and re-base the drift metric so whole-walk SLAM error doesn't
        // keep flagging content that was just placed accurately.
        const p = provider.placeRelativeToUser(t.geo, camera)
        if (p) {
          t.holder.position.copy(t.holder.parent!.worldToLocal(p.clone()))
          provider.rebaseDrift(camera)
        }
        t.holder.visible = true
        t.contentState = 'ready'
        emitStatus()
      })
      .catch((err) => {
        console.warn(`[quest] content load failed for anchor ${t.raw.id}`, err)
        t.contentState = 'failed'
        emitStatus()
      })
  }

  function updateDiscovery(t: TrackedAnchor, dt: number) {
    if (t.discovered || t.distanceM === null) return
    const exitR = radiusM * EXIT_HYSTERESIS
    if (t.inside) {
      if (t.distanceM > exitR) {
        t.inside = false
        t.dwellSeconds = 0
        return
      }
      t.dwellSeconds += dt
      if (t.dwellSeconds >= DWELL_SECONDS) {
        t.discovered = true
        const found = new Set(tracked.filter((x) => x.discovered).map((x) => x.raw.id))
        saveProgress(alternative.id, found, found.size === tracked.length)
        cb.onDiscovered?.(toState(t))
        emitStatus()
      }
    } else if (t.distanceM <= radiusM) {
      t.inside = true
      t.dwellSeconds = 0
    }
  }

  function toState(t: TrackedAnchor): QuestAnchorState {
    return {
      id: t.raw.id,
      name: t.name,
      order: num(t.raw.order) ?? 0,
      discovered: t.discovered,
      contentState: t.contentState,
      distanceM: t.distanceM,
      relativeBearingCssDeg: t.cssDeg,
    }
  }

  function emitStatus() {
    if (stopped) return
    const states = tracked.map(toState).sort((a, b) => a.order - b.order)
    const foundCount = states.filter((s) => s.discovered).length
    cb.onStatus?.({
      alignment: provider.state,
      engineKind,
      anchors: states,
      foundCount,
      total: states.length,
      completed: foundCount === states.length,
      driftSuspected: provider.driftSuspected,
      loading: tracked.filter((t) => t.contentState === 'loading').map((t) => t.name),
      debug: provider.debug,
    })
  }

  // Status at 5 Hz — plenty for HUD + debug overlay without re-render storms.
  const statusTimer = setInterval(emitStatus, 200)
  emitStatus()

  return {
    recenter: () => provider.recenter(),
    resetProgress: () => {
      try {
        localStorage.removeItem(storageKey(alternative.id))
      } catch {
        // ignore
      }
      for (const t of tracked) {
        t.discovered = false
        t.inside = false
        t.dwellSeconds = 0
      }
      emitStatus()
    },
    stop: () => {
      stopped = true
      clearInterval(statusTimer)
      provider.stop()
      engine.stop()
      tracked.forEach((t) => {
        t.content?.dispose()
        t.label?.dispose()
      })
    },
  }
}
